import { useCallback, useEffect, useState } from "react";
import { usePaginatedSearch } from "../../hooks/use-paginated-search";
import { Spinner, Table } from "react-bootstrap";
import moment from "moment";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import { TRANSACTIONS } from "../../lib/config";
import { formatCellValue } from "../../lib/format-cell";
import { TransactionData } from "../../types";
import FormInput from "../../components/FormInput";
import ExportPopover from "../../components/ExportPopover";
import PaginationComponent from "../../components/PaginationComponent";
import searchIcon from "../../assets/icons/search.svg";
import "../../styles/transactions.css";
import TransactionRecordDetailsModal from "./TransactionRecordDetailsModal";

const TransactionTable = () => {
  const [transactions, setTransactions] = useState<TransactionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionData | null>(null);

  const {
    searchTerm,
    debouncedSearchTerm,
    paginationData,
    setPaginationData,
    handleSearchChange,
    handlePaginate
  } = usePaginatedSearch(10);

  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    try {
      api.setAuthorization(api.getLoggedInUser());
      const queryParams = new URLSearchParams({
        page: paginationData.currentPage.toString(),
        per_page: paginationData.perPage.toString(),
        sortBy: 'created_at',
        sortOrder: 'desc',
      });
      if (debouncedSearchTerm.trim()) {
        queryParams.set('search', debouncedSearchTerm.trim());
      }
      if (startDate) {
        queryParams.set('startDate', moment(startDate).format('YYYY-MM-DD'));
      }
      if (endDate) {
        queryParams.set('endDate', moment(endDate).format('YYYY-MM-DD'));
      }
      const response = await api.get(endpoints.getAllTransactions, queryParams);
      const { data, pageInfo } = response?.data?.reports || {};

      setTransactions(data || []);
      setPaginationData({
        currentPage: pageInfo?.currentPage || 1,
        totalPages: pageInfo?.totalPages || 1,
        total: pageInfo?.total || 0,
        perPage: pageInfo?.perPage || 10
      });
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchTerm, paginationData.currentPage, paginationData.perPage, setPaginationData, startDate, endDate]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

   const getSerialNumber = (index: number) =>
    (paginationData.currentPage - 1) * paginationData.perPage + index + 1;

  return (
    <>
      <div className="table-topbar">
        <div className="search_bar">
          <img src={searchIcon} alt="search" />
          <FormInput
            name="search"
            placeholder="Search transactions..."
            className="search-bar-input"
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
          />
        </div>

        <div className="range-input-section mb-3 mt-2">
          <FormInput
            label="From:"
            type="date"
            name="startDate"
            value={startDate}
            onChange={e => {
              setStartDate((e.target as HTMLInputElement).value);
              setPaginationData(prev => ({ ...prev, currentPage: 1 }));
            }}
          />
          <FormInput
            label="To:"
            type="date"
            name="endDate"
            value={endDate}
            onChange={e => {
              setEndDate((e.target as HTMLInputElement).value);
              setPaginationData(prev => ({ ...prev, currentPage: 1 }));
            }}
          />
        </div>
 
        <ExportPopover
          exportConfig="transactions"
          searchQuery={debouncedSearchTerm}
          dateRange={{
            startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
            endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : undefined,
          }}
          // filters={{ status: selectedStatus }}
          // dateRange={{
          //   startDate: dateRange?.startDate,
          //   endDate: dateRange?.endDate
          // }}
          disabled={transactions?.length === 0}
          availableFormats={['csv', 'xlsx', 'pdf']}
          onExportStart={() => console.log('Export started')}
          onExportComplete={() => console.log('Export complete')}
          onExportError={console.error}
        />
      </div>

      <div className="table-body">
        <Table>
          <thead>
            <tr>
              {/* <th className="custom-thead-bg">S/N</th> */}
              {TRANSACTIONS.map(col => (
                <th key={col.key} className="custom-thead-bg">{col.label}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={TRANSACTIONS.length + 2} className="text-center">
                  <Spinner animation="border" size="sm" />
                </td>
              </tr>
            ) : transactions?.length === 0 ? (
              <tr>
                <td colSpan={TRANSACTIONS.length + 2} className="text-center">
                  No transactions found.
                </td>
              </tr>
            ) : (
              transactions?.map((txn, index) => (
                <tr
                  key={txn.id}
                  className="clickable-row"
                  onClick={() => setSelectedTransaction(txn)}
                >
                  {/* <td className="custom-tbody">{getSerialNumber(index)}</td> */}
                  {TRANSACTIONS.map(col => (
                    <td key={col.key} className="custom-tbody">{formatCellValue(col, txn)}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {transactions?.length > 0 && (
        <PaginationComponent
          currentPage={paginationData.currentPage}
          totalPages={paginationData.totalPages}
          hasNextPage={paginationData.currentPage < paginationData.totalPages}
          hasPreviousPage={paginationData.currentPage > 1}
          nextPage={paginationData.currentPage + 1}
          previousPage={paginationData.currentPage - 1}
          onPaginate={handlePaginate}
        />
      )}

      {selectedTransaction && (
        <TransactionRecordDetailsModal
          transaction={selectedTransaction}
          onHide={() => setSelectedTransaction(null)}
        />
      )}
    </>
  );
};

export default TransactionTable;
